import _ from 'lodash';
import { ModalContext } from 'context/ModalContext';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import { MODAL_ANIMATIONS, MODAL_TYPES } from '../constants';
import clsx from 'clsx';
import api from 'services/api';
import NoImg from 'assets/img/no-img.jpg';
import { useLocation } from 'react-router-dom';
import useVideo from 'hooks/useVideo';
import { updateChannelPlaylist } from 'store/activitySlice';
import { addPlaylistsToChannel } from 'store/allPlaylists';
import { fetchPlaylists } from 'api/channelsAPI';
import { CloseIcon } from 'components/icons/CloseIcon';

interface ISelectPlaylist {
  selectedChannel: any;
  onCancel: () => void;
  videoId: string;
  videoHashId: string;
}

const SelectPlaylist = (props: ISelectPlaylist) => {
  const { showModal } = useContext(ModalContext);
  const { hideModal } = useContext(ModalContext);
  const [searchVal, setSearchVal] = useState('');
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  const selectedChannel = channelList.find((channel: any) => channel.id === props.selectedChannel.id);
  const dispatch = useDispatch();
  const playlistSlice = useSelector((state: RootState) => state.allPlaylist.channels);
  const initialPlaylistStates = [...(playlistSlice[selectedChannel?.hash_id]?.playlists || [])].sort(
    (a: { name: string }, b: { name: string }) => {
      if (searchVal) {
        if (
          a.name.toLowerCase().indexOf(searchVal.toLowerCase()) < b.name.toLowerCase().indexOf(searchVal.toLowerCase())
        ) {
          return -1;
        }
        if (
          a.name.toLowerCase().indexOf(searchVal.toLowerCase()) > b.name.toLowerCase().indexOf(searchVal.toLowerCase())
        ) {
          return 1;
        }
      } else {
        if (a.name.toLowerCase() < b.name.toLowerCase()) {
          return -1;
        }
        if (a.name.toLowerCase() > b.name.toLowerCase()) {
          return 1;
        }
      }
      return 0;
    }
  );
  // .filter((playlist: any) => {
  //   if (playlistSlice[selectedChannel?.hash_id]?.playlists.find((item: any) => item.id === playlist.id)) {
  //     return true;
  //   }
  //   return false;
  // });
  const [playlistStates, handlePlaylistStates] = useState(initialPlaylistStates);
  const [selectedPlaylists, setSelectedPlaylists] = useState<number[]>([]);

  const location = useLocation();
  const channelIdRegex = /\/channel\/([^/]+)/;
  const channelMatch = location.pathname.match(channelIdRegex);
  const channelId = channelMatch ? channelMatch[1] : 'home';

  const [sharedChannels, handleSharedChannels] = useState([] as any[]);
  const userData = useSelector((state: RootState) => state.user.user);
  const [shared, setShared] = useState(props.selectedChannel.share || (channelId === 'home' ? false : true));

  const [selectedVideo, setSelectedVideo] = useState(null as any);
  const { video, loading, selectVideo } = useVideo(props.videoHashId, channelId, props.videoId);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (selectedChannel.hash_id) {
        const data = await fetchPlaylists(selectedChannel.hash_id);
        dispatch(addPlaylistsToChannel({ channelHashId: selectedChannel.hash_id, playlists: data }));
      }
    };

    if (selectedChannel) {
      if (!playlistSlice[selectedChannel.hash_id]) {
        fetchData();
      }
    }
  }, [selectedChannel]);

  useEffect(() => {
    if (selectVideo) {
      setSelectedVideo(selectVideo);
    } else {
      setSelectedVideo(video);
    }
  }, [loading]);

  const createPlaylist = () => {
    showModal(MODAL_TYPES.create_playlist, {
      animation: MODAL_ANIMATIONS.fade,
      selectedChannel: props.selectedChannel,
      videoId: props.videoId,
      videoHashId: props.videoHashId
    });
  };

  const closeSelectPlaylist = () => {
    showModal(MODAL_TYPES.share_video, {
      animation: MODAL_ANIMATIONS.fade,
      initChannel: props.selectedChannel,
      videoId: props.videoId,
      videoHashId: props.videoHashId
    });
  };

  const shareOrSave = async () => {
    await api
      .post(`api/videos/playlist/`, {
        action: 'update',
        channel: selectedChannel.hash_id,
        playlists: selectedPlaylists,
        video: props.videoHashId
      })
      .then((resp) => {
        const payload = {
          key: selectedChannel.hash_id,
          id: props.videoId,
          playlists: resp.data
        };
        dispatch(updateChannelPlaylist(payload));
        props.onCancel();
      });
    props.onCancel();
  };

  useEffect(() => {
    api.get(`api/videos/${props.videoId}/shares/`).then((res) => {
      if (selectedChannel) {
        const sharedChannel = res.data.find((item: any) => item.channel_hash_id === selectedChannel.hash_id);
        if (sharedChannel) {
          setShared(true);
          handlePlaylistStates(sharedChannel.playlists);
          setSelectedPlaylists(sharedChannel.playlists.map((item: any) => item.id));
        } else {
          setShared(false);
        }
      }
      handleSharedChannels(res.data);
      setIsLoading(false);
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  const handleCheckboxChange = (position: number, event: any) => {
    if (event.target.checked) {
      const playlists = playlistSlice[selectedChannel?.hash_id]?.playlists;
      setSelectedPlaylists((prev) => [...prev, Number(event.target.value)]);
      if (playlists) {
        handlePlaylistStates((prev) => [...prev, playlists.find((playlist: any) => playlist.id == event.target.value)]);
      }
    } else {
      setSelectedPlaylists((prev) => [...prev].filter((value) => value != event.target.value));
      handlePlaylistStates((prev) => [...prev].filter((value) => value.id != event.target.value));
    }
  };

  return (
    <div className="transparent font-lato">
      <div className="relative border bg-gray-bg-dark border-gray-300 rounded-lg bg-white pb-7 pt-9 px-7 w-screen md:w-[622px] md:min-h-[300px]">
        <div className="flex">
          <span className="font-bold text-2xl leading-6 text-[#1A1C1D]">Select Collection</span>

          <CloseIcon className="ml-auto w-5 h-5 cursor-pointer opacity-70 hover:opacity-100" onClick={hideModal} />
        </div>
        <div className="relative">
          <input
            type="text"
            className="px-4 py-2 w-full mb-4 text-sm text-[#495057] border focus:ring-0 border-[#ced4da] rounded-2xl leading-6 mt-8"
            onChange={handleChange}
          />
          {searchVal ? (
            ''
          ) : (
            <span className="absolute top-11 text-gray-main text-sm left-4 pointer-events-none">
              Search for a collection in
              <span className="text-blue-menu-active"> #{selectedChannel?.name}</span>
            </span>
          )}
        </div>
        <div className="overflow-auto border rounded-md px-4 py-2 h-40 overflow-y-auto">
          {selectedChannel &&
            !isLoading &&
            playlistSlice[selectedChannel?.hash_id] &&
            playlistSlice[selectedChannel?.hash_id]?.playlists
              .filter((item: any) => item.name.toLowerCase().includes(searchVal.toLowerCase()))
              .sort((a: { name: string }, b: { name: string }) => {
                if (searchVal) {
                  if (
                    a.name.toLowerCase().indexOf(searchVal.toLowerCase()) <
                    b.name.toLowerCase().indexOf(searchVal.toLowerCase())
                  ) {
                    return -1;
                  }
                  if (
                    a.name.toLowerCase().indexOf(searchVal.toLowerCase()) >
                    b.name.toLowerCase().indexOf(searchVal.toLowerCase())
                  ) {
                    return 1;
                  }
                } else {
                  if (a.name.toLowerCase() < b.name.toLowerCase()) {
                    return -1;
                  }
                  if (a.name.toLowerCase() > b.name.toLowerCase()) {
                    return 1;
                  }
                }
                return 0;
              })
              .map((playlist: any, index: number) => (
                <div className="flex items-center mt-1" key={uuid()}>
                  <input
                    type="checkbox"
                    id={`playlist-checkbox-${index}`}
                    className="focus:ring-0 rounded-sm w-[14px] h-[14px]"
                    value={playlist.id}
                    name={playlist.name}
                    checked={selectedPlaylists.includes(playlist.id)}
                    onChange={(event) => handleCheckboxChange(index, event)}
                  />
                  <label
                    htmlFor={`playlist-checkbox-${index}`}
                    className="text-base leading-4 text-[#212529] font-normal font-lato pl-2"
                  >
                    {playlist.name}
                  </label>
                </div>
              ))}
        </div>
        <div className="mt-6 font-lato text-sm leading-[21px] font-bold">
          <p>Title: {selectedVideo?.name}</p>
          <div className="border rounded-md p-2 mt-4 h-20 overflow-y-auto">
            {sharedChannels.filter((channel: any) => !channel.is_archived).length ? (
              <p className="text-[#8083A3] line-clamp-3">
                Shared in
                {sharedChannels
                  .filter((channel: any) => !channel.is_archived)
                  .map(
                    (channel: any) => channelList.find((item: any) => item.hash_id === channel.channel_hash_id)?.name
                  )
                  .filter((item) => item)
                  .map((item: string, idx: number) => (
                    <span className="text-[#1664A3]" key={uuid()}>
                      {idx !== 0 && ', '}
                      {' #'}
                      {item}
                    </span>
                  ))}
              </p>
            ) : (
              <p className="text-[#8083A3]">Not shared in any channels</p>
            )}
          </div>
        </div>
        <div className="flex items-center mt-5">
          <img
            className="rounded-full cursor-pointer w-8 h-8 mr-2"
            src={selectedVideo?.owner?.avatar ? selectedVideo.owner.avatar : NoImg}
            alt="avatar"
          />
          <div className="text-sm text-[#1A1C1D]">
            <p className="font-bold">
              Owned by {selectedVideo?.owner.user_id === userData.user_id ? 'you' : selectedVideo?.owner?.name}
            </p>
          </div>
        </div>
        <div className="flex mt-5">
          <div
            className="w-40 h-11 rounded-[10px] text-base font-bold flex items-center justify-center bg-green-600 text-white cursor-pointer"
            onClick={createPlaylist}
          >
            Create a collection
          </div>
          <div
            className={clsx(
              'w-24 h-11 rounded-[10px] text-base font-bold flex items-center justify-center ml-auto bg-green-600 text-white cursor-pointer'
            )}
            onClick={closeSelectPlaylist}
          >
            Cancel
          </div>
          {shared ? (
            <div
              className={clsx('w-40 h-11 rounded-[10px] text-base font-bold flex items-center justify-center ml-2', {
                'bg-green-600 text-white cursor-pointer': !_.isEqual(playlistStates, initialPlaylistStates),
                'bg-buttonDisabledBg text-white pointer-events-none': _.isEqual(playlistStates, initialPlaylistStates)
              })}
              onClick={shareOrSave}
            >
              Save
            </div>
          ) : (
            <div
              className="w-24 h-11 bg-green-600 rounded-[10px] cursor-pointer text-white text-base font-bold flex items-center justify-center ml-2"
              onClick={shareOrSave}
            >
              Share
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectPlaylist;
